'use client';

import { ShieldCheck, Scan, FileCheck, Link2, MapPin, Clock, Award } from 'lucide-react';

const STEPS = [
  {
    num: '01',
    icon: MapPin,
    title: 'Origine',
    text: "Traçabilité de la peau depuis le ranch DALLU NGAAY au Sénégal ou les meilleures tanneries italiennes et françaises. Chaque cuir est enregistré avec son numéro de lot, sa date de tannage et son origine géographique.",
  },
  {
    num: '02',
    icon: Scan,
    title: 'Tannage',
    text: "Tannage végétal pendant 42 jours, sans produits chimiques agressifs. Certificat délivré par la Fédération Européenne des Tanneurs. Chaque lot est analysé : pH, résistance, épaisseur, texture.",
  },
  {
    num: '03',
    icon: FileCheck,
    title: 'Façonnage',
    text: "Chaque pièce est numérotée à la main par le maître artisan. Son parcours est enregistré : coupe, couture sellier, patine, finition. 180 étapes documentées, 10 points de contrôle qualité.",
  },
  {
    num: '04',
    icon: ShieldCheck,
    title: 'Certification',
    text: "Certificat d'authenticité numérique signé et horodaté. Gravure laser du numéro unique sur la pièce. Enregistrement sur registre permanent de la maison MATAR STYLE CUIR.",
  },
  {
    num: '05',
    icon: Link2,
    title: 'Blockchain',
    text: "Vos données sont inscrites sur une blockchain privée Smartest Capital. À tout moment, vous pouvez vérifier l'authenticité, l'origine et le parcours complet de votre pièce via un QR code gravé.",
  },
  {
    num: '06',
    icon: Award,
    title: 'Suivi à vie',
    text: "Réparation, reconditionnement, traçabilité des propriétaires successifs. La garantie à vie MATAR couvre tous les défauts de fabrication et permet la transmission d'une génération à l'autre.",
  },
];

const STATS = [
  { value: '180', label: 'Étapes documentées' },
  { value: '42', label: 'Jours de tannage' },
  { value: '10', label: 'Points de contrôle' },
  { value: '100%', label: 'Traçabilité blockchain' },
];

export default function Traceability() {
  return (
    <section
      id="tracabilite"
      className="relative py-24 sm:py-32 bg-[#0a0a0f] border-t border-[#D4AF37]/10 overflow-hidden"
    >
      {/* Decorative gold accent */}
      <div
        className="absolute -top-1/3 -left-1/4 w-[700px] h-[700px] rounded-full opacity-[0.06] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #D4AF37 0%, transparent 70%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block h-px w-10 bg-[#D4AF37]" />
            <span className="eyebrow">Traçabilité & Blockchain</span>
            <span className="block h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
            <span className="text-gold-gradient">Chaque pièce a une histoire,</span>
            <br />
            <span className="text-[#F4E8C1]">vérifiable à jamais.</span>
          </h2>
          <p className="max-w-3xl mx-auto text-[#F4E8C1]/65 font-light leading-relaxed">
            En partenariat avec la blockchain Smartest Capital, MATAR STYLE CUIR
            garantit l'origine, le façonnage et l'authenticité de chaque pièce.
            De la peau à votre main, tout est traçable, vérifiable, immuable.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-20">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="text-center bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-6 hover:border-[#D4AF37]/45 transition-colors"
            >
              <div className="font-display text-3xl sm:text-4xl font-bold text-gold-gradient">
                {s.value}
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/60 mt-2">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Process steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {STEPS.map((step) => (
            <div
              key={step.num}
              className="group relative bg-[#12121a]/60 backdrop-blur-sm border border-[#D4AF37]/20 p-7 hover:border-[#D4AF37]/50 transition-colors duration-400"
            >
              {/* Top row */}
              <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 rounded-full border border-[#D4AF37]/40 flex items-center justify-center group-hover:bg-gold-gradient transition-colors duration-400">
                  <step.icon
                    size={20}
                    className="text-[#D4AF37] group-hover:text-[#0a0a0f] transition-colors"
                  />
                </div>
                <div className="font-display text-3xl font-bold text-gold-gradient opacity-70">
                  {step.num}
                </div>
              </div>

              <h3 className="font-display text-lg font-semibold text-[#F4E8C1] mb-3 tracking-wide">
                {step.title}
              </h3>
              <p className="text-sm text-[#F4E8C1]/65 leading-relaxed font-light">
                {step.text}
              </p>

              {/* Bottom decoration */}
              <div className="mt-5 pt-4 border-t border-[#D4AF37]/12 flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]/70 flex items-center gap-1.5">
                  <Clock size={11} />
                  Étape {step.num}/06
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* QR code demo block */}
        <div className="mt-16 bg-gradient-to-br from-[#12121a] to-[#0a0a0f] border border-[#D4AF37]/30 p-8 sm:p-10 grid md:grid-cols-2 gap-8 items-center">
          {/* Left — text */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Link2 size={18} className="text-[#D4AF37]" />
              <span className="eyebrow">Vérifiez en un scan</span>
            </div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#F4E8C1] mb-4 leading-tight">
              Un QR code gravé,
              <br />
              <span className="text-gold-gradient">une vérité immuable.</span>
            </h3>
            <p className="text-[#F4E8C1]/70 leading-relaxed font-light text-sm mb-5">
              Chaque pièce MATAR porte un QR code unique gravé au dos. Scannez-le
              avec votre téléphone pour accéder au dossier complet de votre
              acquisition : origine du cuir, nom de l'artisan, date de façonnage,
              numéro de série, certificat d'authenticité, historique des
              réparations.
            </p>
            <div className="flex flex-wrap gap-3">
              {['Origine cuir', 'Artisan', 'Date façonnage', 'N° série', 'Certificat', 'Réparations'].map((item) => (
                <span
                  key={item}
                  className="text-[10px] uppercase tracking-[0.18em] text-[#F4E8C1]/75 border border-[#D4AF37]/30 px-3 py-1.5"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Right — mock QR */}
          <div className="flex flex-col items-center md:items-end">
            <div className="relative bg-[#F4E8C1] p-4 sm:p-5 w-44 h-44 sm:w-56 sm:h-56">
              {/* Mock QR pattern */}
              <div
                className="w-full h-full"
                style={{
                  backgroundImage: `
                    repeating-linear-gradient(0deg, #0a0a0f 0 4px, transparent 4px 8px),
                    repeating-linear-gradient(90deg, #0a0a0f 0 4px, transparent 4px 8px)
                  `,
                  backgroundSize: '8px 8px',
                }}
              />
              {/* Corner markers */}
              <div className="absolute top-2 left-2 w-8 h-8 border-4 border-[#0a0a0f] bg-[#F4E8C1]" />
              <div className="absolute top-2 right-2 w-8 h-8 border-4 border-[#0a0a0f] bg-[#F4E8C1]" />
              <div className="absolute bottom-2 left-2 w-8 h-8 border-4 border-[#0a0a0f] bg-[#F4E8C1]" />
            </div>
            <div className="mt-4 text-center md:text-right">
              <div className="font-display text-sm font-semibold text-[#D4AF37] tracking-[0.2em]">
                MATAR · MSC-2024-00891
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/55 mt-1">
                Exemple de QR gravé
              </div>
            </div>
          </div>
        </div>

        {/* Final manifesto */}
        <div className="mt-14 text-center max-w-3xl mx-auto">
          <ShieldCheck size={32} className="text-[#D4AF37] mx-auto mb-4" />
          <p className="font-serif italic text-lg sm:text-xl text-[#F4E8C1]/85 leading-relaxed mb-5">
            « La véritable traçabilité n'est pas un certificat de plus. C'est la
            promesse qu'aucune main invisible n'a touché votre pièce, qu'aucune
            machine n'a trahi le geste, et que votre héritage restera pur. »
          </p>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
            — Manifeste Traçabilité MATAR, 2024
          </p>
        </div>
      </div>
    </section>
  );
}
